/* eslint-disable camelcase */
import { observer } from "mobx-react-lite"
import React, { FC, useCallback } from "react"
import { Appearance, TouchableOpacity, useWindowDimensions, ViewStyle } from "react-native"
import {
  FlatList, JalaIcon,
  ListEmpty,
  ListRenderItem,
  ListRenderItemInfo,
  Screen,
  Text,
} from "../../../../components"
import numeral from "numeral"
import { useStores } from "../../../../models"
import { BatchStoreModel } from "../../../../models/app-store/farm-store/batch-store/batch-store"
import { FarmStoreModel } from "../../../../models/app-store/farm-store/farm-store"
import { color, nativeBaseTheme } from "../../../../theme"
import { SafeAreaView } from "react-native-safe-area-context"
import { Icon, View } from "native-base"
import _ from "lodash"
import { translate } from "../../../../i18n"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const HEADER: ViewStyle = {
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row',
  paddingVertical: 8,
  alignItems: 'center'
}

const CONTENT_ITEM: ViewStyle = {
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  paddingVertical: 10,
  flexDirection: 'row'
}

export const OverviewTotalWeightPredictionListRefresh: FC = observer(function OverviewTotalWeightPredictionListRefresh(props) {
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})

  const onPress = useCallback(() => {
    activeBatchStore.init()
  }, [activeBatchStore])

  return (
    <View justifyContent="center">
      <View paddingRight="16px" paddingLeft="0px">
        <TouchableOpacity onPress={onPress}>
          <Icon as={JalaIcon} name="refresh" size="24px" color={color.palette.white} />
        </TouchableOpacity>
      </View>
    </View>
  )
})

const Header: FC<{ colorScheme?: "light"|"dark" }> = observer(function Header(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { predictionTotalWeight, cycles, isFeedActualWithin4Days, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})

  return (
    <View paddingX="16px" paddingTop="8px" paddingBottom="15px">
      <Text text={farmStore.farm.name} preset="title3" marginBottom="4px" colorScheme={colorScheme} />
      <Text tx="overviewScreen.total" preset="body3" colorScheme={colorScheme} color={nativeBaseTheme.colors.gray[colorScheme === 'light' ? '700' : '300']} />
      <View flexDirection="row" alignItems="center">
        <Text
          text={numeral(predictionTotalWeight).format('0,0.[00]')}
          preset="display1"
          marginRight="3px"
          colorScheme={colorScheme}
          color={loadingCyclePrediction && color.palette.dust500 || undefined}
        />
        <Text text="kg" preset="display2" marginRight="8px" colorScheme={colorScheme} color={color.palette.dust500} />
      </View>
      <Text text={translate("overviewScreen.fromActivePonds", { count: cycles.length })} preset="body3" colorScheme={colorScheme} color={color.palette.dust500} />
      {!isFeedActualWithin4Days && <Text
        tx="overviewScreen.disclaimer"
        preset="body3"
        colorScheme={colorScheme}
        color={nativeBaseTheme.colors.danger["500"]}
      />}
    </View>
  )
})

const TableHeader: FC<{ colorScheme?: "light"|"dark" }> = (props) => {
  const { colorScheme } = props;

  return (
    <View style={HEADER}>
      <View width="10%" paddingX="8px" alignItems="flex-start">
        <Text text="#" preset="body3" transformText={_.upperFirst} colorScheme={colorScheme} />
      </View>
      <View width="30%" paddingX="8px" alignItems="flex-start">
        <Text tx="cyclePredictions.pond" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="15%" paddingX="8px" alignItems="flex-start">
        <Text tx="cyclePredictions.age" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="20%" paddingX="4px" alignItems="flex-start">
        <Text tx="cyclePredictions.weight" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="25%" paddingX="4px" alignItems="flex-start">
        <Text tx="cyclePredictions.total_weight" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
    </View>
  )
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any> }> = observer(function Item(props) {
  const { colorScheme, info } = props
  const { index, item: { age, pond_name, cycle_predictions } } = info;
  const { weight, total_weight } = cycle_predictions.find((c) => c.age <= age) || {};
  return (
    <View style={CONTENT_ITEM}>
      <View width="10%" paddingX="8px" alignItems="flex-start">
        <Text text={`${index + 1}`} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="30%" paddingX="8px" alignItems="flex-start">
        <Text text={pond_name} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="15%" paddingX="8px" alignItems="flex-start">
        <Text text={`${age}`} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="20%" paddingX="4px" alignItems="flex-start">
        <Text text={numeral(weight).format('0,0.[00]')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="25%" paddingX="4px" alignItems="flex-start">
        <Text text={numeral(total_weight).format('0,0.[00]')} preset="body3" colorScheme={colorScheme} />
      </View>
    </View>
  )
});

export const OverviewTotalWeightPredictionListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function OverviewTotalWeightPredictionList(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { cycles, loadingCyclePrediction } = activeBatchStore || BatchStoreModel.create({})

  const keyExtractor = useCallback((item, index) => `${item.id || item.started_at || index}`, [])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} />
  }, [colorScheme])

  const onRefresh = useCallback(() => {
    activeBatchStore.init()
  }, [activeBatchStore])

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: home - overview - estimasi Biomasa');
  }, []))

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      <FlatList
        data={cycles}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={<>
          <Header colorScheme={colorScheme} />
          <TableHeader colorScheme={colorScheme} />
        </>}
        ListEmptyComponent={<ListEmpty colorScheme={colorScheme} height={`${height - 270}px`} />}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={!!loadingCyclePrediction}
        estimatedItemSize={37}
      />
    </Screen>
  )
})
